import { useEffect, useRef, useState } from 'react';

/** A region of a panel's screenshot, as fractions of its width and height. */
export type FocusRegion = { x: number; y: number; w: number; h: number };

export type SurfacePanel = {
  id: string;
  title: string;
  lead: string;
  img: string;
  alt: string;
  cards: { title: string; description: string }[];
  icons: readonly string[];
  /** Where each card points in the screenshot; a card without a region keeps the full view. */
  focus?: readonly FocusRegion[];
  /** Tailwind classes for the feature icon chip. */
  chip: string;
  /** Chip classes while its card is the one in focus. */
  chipActive: string;
  /** Border colour of the spotlight drawn over the screenshot. */
  spot: string;
  /** Section icon shown in the switcher. */
  icon: string;
  /** Soft background tint for the card while this panel is active. */
  tint: string;
};

/** Scroll distance given to each beat (a panel overview or one feature), in vh. */
const BEAT_VH = 25;
const FULL_VIEW: FocusRegion = { x: 0, y: 0, w: 1, h: 1 };
const MAX_ZOOM = 1.6;
/** Share of the frame a focused region may fill before the zoom stops. */
const ZOOM_FILL = 0.84;

/** Transform that centres `r` in the frame, zoomed in as far as it fits, without exposing an edge. */
function zoomTo(r: FocusRegion) {
  const s = Math.min(MAX_ZOOM, Math.max(1, Math.min(ZOOM_FILL / r.w, ZOOM_FILL / r.h)));
  const shift = (start: number, size: number) => Math.min(0, Math.max(1 - s, 0.5 - s * (start + size / 2)));
  return `translate(${shift(r.x, r.w) * 100}%, ${shift(r.y, r.h) * 100}%) scale(${s})`;
}

/**
 * Desktop scroll story for Command centre + Copilot: the section pins to one screen and scrolling
 * through it walks each panel's features, zooming the screenshot to the part each one describes,
 * before switching panels inside the same card. `#copilot` still lands on the second panel.
 */
export default function SurfacesScrolly({
  panels,
  kicker,
  title,
  intro,
}: {
  panels: SurfacePanel[];
  kicker: string;
  title: string;
  intro: string;
}) {
  const [progress, setProgress] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const n = panels.length;

  // Each panel is an overview beat followed by one beat per feature card.
  const beats = panels.map((p) => p.cards.length + 1);
  const starts = beats.map((_, i) => beats.slice(0, i).reduce((a, b) => a + b, 0));
  const total = Math.max(1, beats.reduce((a, b) => a + b, 0));
  const beat = Math.min(total - 1, Math.floor(progress * total));
  const active = starts.reduce((last, s, i) => (s <= beat ? i : last), 0);
  /** Card in focus within the active panel; -1 during the overview beat. */
  const focused = beat - starts[active] - 1;

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = trackRef.current;
      if (!el) return;
      const range = el.offsetHeight - window.innerHeight;
      const p = range > 0 ? -el.getBoundingClientRect().top / range : 0;
      setProgress(Math.min(1, Math.max(0, p)));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const goTo = (b: number) => {
    const el = trackRef.current;
    if (!el) return;
    const range = el.offsetHeight - window.innerHeight;
    const top = el.getBoundingClientRect().top + window.scrollY + (range * (b + 0.5)) / total;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div ref={trackRef} className="relative" style={{ height: `${total * BEAT_VH + 100}vh` }}>
      {/* Anchors for in-page links, placed inside each panel's overview beat. */}
      {panels.slice(1).map((p, k) => (
        <span
          key={p.id}
          id={p.id}
          className="absolute start-0 scroll-mt-16"
          style={{ top: `${(starts[k + 1] + 0.7) * BEAT_VH}vh` }}
          aria-hidden
        />
      ))}

      <div className="sticky top-16 flex h-[calc(100vh-4rem)] flex-col justify-center py-4">
        <div className="mx-auto w-full max-w-7xl px-6">
          <header className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">{kicker}</p>
            <h2 id="hiw-surfaces-heading" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              {title}
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed text-gray-600 [@media(max-height:860px)]:hidden">{intro}</p>
          </header>

          {/* Switcher: each tab fills as you scroll through its panel. */}
          <div className="mx-auto mt-5 flex w-fit gap-1 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm" role="tablist">
            {panels.map((p, i) => {
              const fill = Math.min(1, Math.max(0, (progress * total - starts[i]) / beats[i]));
              const on = i === active;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => goTo(starts[i])}
                  className={`relative flex items-center gap-2 overflow-hidden rounded-xl px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                    on ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <i className={`${p.icon} text-base`} aria-hidden />
                  {p.title}
                  <span className="absolute inset-x-3 bottom-1 h-0.5 overflow-hidden rounded-full bg-white/15" aria-hidden>
                    <span
                      className={`block h-full origin-left rounded-full rtl:origin-right ${on ? 'bg-white/70' : 'bg-transparent'}`}
                      style={{ transform: `scaleX(${fill})` }}
                    />
                  </span>
                </button>
              );
            })}
          </div>

          <div className="relative mt-5 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-gray-900/[0.04]">
            {/* Tints crossfade with the active panel. */}
            {panels.map((p, i) => (
              <div
                key={p.id}
                className={`pointer-events-none absolute inset-0 transition-opacity duration-700 ${p.tint} ${i === active ? 'opacity-100' : 'opacity-0'}`}
                aria-hidden
              />
            ))}

            <div className="relative grid min-h-[min(60vh,560px)] grid-cols-12">
              {/* Copy: the outgoing block fades out before the incoming one fades in. */}
              <div className="col-span-5 grid content-center px-8 py-7 xl:px-10">
                {panels.map((p, i) => {
                  const offset = i - active;
                  const focusedCard = offset === 0 ? focused : -1;
                  return (
                    <div
                      key={p.id}
                      inert={offset !== 0}
                      aria-hidden={offset !== 0}
                      className={`[grid-area:1/1] transition-all ease-out ${
                        offset === 0
                          ? 'translate-y-0 opacity-100 delay-200 duration-500'
                          : `${offset < 0 ? '-translate-y-3' : 'translate-y-3'} opacity-0 duration-200`
                      }`}
                    >
                      <span className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400 tabular-nums">
                        {String(i + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
                      </span>
                      <h3 className="mt-2 text-2xl font-bold tracking-tight text-gray-900 xl:text-[28px]">{p.title}</h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-gray-600">{p.lead}</p>
                      {/* Features: the one in focus lifts onto a card while the rest step back. */}
                      <ul className="m-0 mt-5 list-none space-y-3.5 p-0">
                        {p.cards.map((card, j) => {
                          const isFocused = j === focusedCard;
                          const dimmed = focusedCard >= 0 && !isFocused;
                          return (
                            <li key={card.title} className="relative">
                              <span
                                className={`absolute -inset-x-3 -inset-y-1.5 rounded-xl bg-white shadow-md shadow-gray-900/[0.06] ring-1 ring-gray-900/[0.06] transition-all duration-500 ${
                                  isFocused ? 'scale-100 opacity-100' : 'scale-[0.98] opacity-0'
                                }`}
                                aria-hidden
                              />
                              <button
                                type="button"
                                onClick={() => goTo(starts[i] + j + 1)}
                                aria-current={isFocused ? 'true' : undefined}
                                className={`relative flex w-full gap-3 text-start transition-opacity duration-500 ${
                                  dimmed ? 'opacity-45 hover:opacity-80' : 'opacity-100'
                                }`}
                              >
                                <span
                                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-500 ${
                                    isFocused ? p.chipActive : p.chip
                                  }`}
                                >
                                  <i className={`${p.icons[j] ?? 'ri-layout-grid-line'} text-base`} aria-hidden />
                                </span>
                                <span className="block min-w-0">
                                  <span className="block text-sm font-semibold text-gray-900">{card.title}</span>
                                  <span className="mt-0.5 line-clamp-2 block text-[13px] leading-snug text-gray-600">
                                    {card.description}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Screenshot stage: the outgoing panel recedes up and back as the next one rises in. */}
              <div className="relative col-span-7 flex items-center justify-center py-8 pe-8">
                <div
                  className="relative aspect-[1564/1006] w-full"
                  style={{ maxWidth: 'calc((min(60vh, 560px) - 4rem) * 1.555)' }}
                >
                  {panels.map((p, i) => {
                    const offset = i - active;
                    const region = offset === 0 && focused >= 0 ? p.focus?.[focused] : undefined;
                    const box = region ?? FULL_VIEW;
                    return (
                      <div
                        key={p.id}
                        className={`absolute inset-0 overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl shadow-gray-900/10 ring-1 ring-black/[0.03] transition-all duration-700 ease-out ${
                          offset === 0
                            ? 'z-10 translate-x-0 translate-y-0 scale-100 opacity-100'
                            : offset < 0
                              ? 'z-0 -translate-x-[6%] -translate-y-[8%] scale-[0.92] opacity-0'
                              : 'z-0 translate-y-[6%] scale-[0.96] opacity-0'
                        }`}
                        aria-hidden={offset !== 0}
                      >
                        {/* Zoom layer: glides to the region the focused feature describes. */}
                        <div
                          className="absolute inset-0 origin-top-left transition-transform duration-700 ease-out"
                          style={{ transform: zoomTo(box) }}
                        >
                          <img src={p.img} alt={offset === 0 ? p.alt : ''} className="h-full w-full object-contain" />
                          {/* Spotlight: its oversized shadow dims everything outside the region. */}
                          <span
                            className={`absolute rounded-lg border-2 shadow-[0_0_0_100vmax_rgba(15,23,42,0.28)] transition-all duration-700 ease-out ${p.spot} ${
                              region ? 'opacity-100' : 'opacity-0'
                            }`}
                            style={{
                              left: `${box.x * 100}%`,
                              top: `${box.y * 100}%`,
                              width: `${box.w * 100}%`,
                              height: `${box.h * 100}%`,
                            }}
                            aria-hidden
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
